import { z } from 'zod'
import type { CrudField, CrudFormGroup } from '@open-mercato/ui/backend/CrudForm'

type Translate = (key: string, fallback: string, params?: Record<string, string | number>) => string

export type ECmrFormValues = {
  issueDate?: string
  referenceNumber?: string
  dispatchId?: string
  driverId?: string
  vehicleId?: string
  senderName?: string
  senderCompany?: string
  senderStreet?: string
  senderCity?: string
  senderPostalCode?: string
  senderCountryCode?: string
  carrierName?: string
  carrierCompany?: string
  carrierStreet?: string
  carrierCity?: string
  carrierPostalCode?: string
  carrierCountryCode?: string
  receiverName?: string
  receiverCompany?: string
  receiverStreet?: string
  receiverCity?: string
  receiverPostalCode?: string
  receiverCountryCode?: string
  placeOfTakingOver?: string
  placeOfDelivery?: string
  plannedPickupAt?: string
  plannedDeliveryAt?: string
  goodsDescription?: string
  goodsPackagesCount?: string
  goodsGrossWeightKg?: string
  goodsVolumeM3?: string
  goodsUnNumber?: string
  goodsHsCode?: string
  goodsMarksAndNumbers?: string
  vehicleRegistrationNumber?: string
  trailerRegistrationNumber?: string
  carrierInstructions?: string
  receiverInstructions?: string
}

function toNullableText(value: unknown): string | null {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

function toNullableNumber(value: string | undefined): number | null {
  const text = toNullableText(value)
  if (!text) return null
  const parsed = Number(text)
  return Number.isFinite(parsed) ? parsed : null
}

function toIsoDateTime(value: string | undefined): string | null {
  const text = toNullableText(value)
  if (!text) return null
  const date = new Date(text)
  if (Number.isNaN(date.getTime())) return null
  return date.toISOString()
}

function buildPartySnapshot(values: {
  name?: string
  company?: string
  street?: string
  city?: string
  postalCode?: string
  countryCode?: string
}) {
  return {
    name: toNullableText(values.name) ?? '',
    company: toNullableText(values.company),
    street: toNullableText(values.street),
    city: toNullableText(values.city),
    postalCode: toNullableText(values.postalCode),
    countryCode: toNullableText(values.countryCode),
    locode: null,
    contactPhone: null,
    contactEmail: null,
    taxId: null,
  }
}

export function buildECmrFormSchema(t: Translate) {
  return z.object({
    issueDate: z.string().min(1, t('ecmr.validation.issueDate', 'Issue date is required.')),
    senderName: z.string().min(1, t('ecmr.validation.senderName', 'Sender name is required.')),
    carrierName: z.string().min(1, t('ecmr.validation.carrierName', 'Carrier name is required.')),
    receiverName: z.string().min(1, t('ecmr.validation.receiverName', 'Receiver name is required.')),
    placeOfTakingOver: z.string().min(1, t('ecmr.validation.placeOfTakingOver', 'Place of taking over is required.')),
    placeOfDelivery: z.string().min(1, t('ecmr.validation.placeOfDelivery', 'Place of delivery is required.')),
    goodsDescription: z.string().min(1, t('ecmr.validation.goodsDescription', 'Goods description is required.')),
    plannedPickupAt: z.string().optional(),
    plannedDeliveryAt: z.string().optional(),
  }).superRefine((values, ctx) => {
    if (values.plannedPickupAt && values.plannedDeliveryAt) {
      const pickup = new Date(values.plannedPickupAt)
      const delivery = new Date(values.plannedDeliveryAt)
      if (!Number.isNaN(pickup.getTime()) && !Number.isNaN(delivery.getTime()) && delivery < pickup) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['plannedDeliveryAt'],
          message: t('ecmr.validation.deliveryAfterPickup', 'Planned delivery must be after pickup.'),
        })
      }
    }
  })
}

export function buildECmrFields(t: Translate): CrudField[] {
  return [
    { id: 'issueDate', label: t('ecmr.form.issueDate', 'Issue date'), type: 'date', required: true },
    { id: 'referenceNumber', label: t('ecmr.form.referenceNumber', 'Reference number'), type: 'text' },
    { id: 'dispatchId', label: t('ecmr.form.dispatchId', 'Dispatch ID'), type: 'text' },
    { id: 'driverId', label: t('ecmr.form.driverId', 'Driver ID'), type: 'text' },
    { id: 'vehicleId', label: t('ecmr.form.vehicleId', 'Vehicle ID'), type: 'text' },
    { id: 'senderName', label: t('ecmr.form.senderName', 'Sender name'), type: 'text', required: true },
    { id: 'senderCompany', label: t('ecmr.form.senderCompany', 'Sender company'), type: 'text' },
    { id: 'senderStreet', label: t('ecmr.form.senderStreet', 'Sender street'), type: 'text' },
    { id: 'senderCity', label: t('ecmr.form.senderCity', 'Sender city'), type: 'text' },
    { id: 'senderPostalCode', label: t('ecmr.form.senderPostalCode', 'Sender postal code'), type: 'text' },
    { id: 'senderCountryCode', label: t('ecmr.form.senderCountryCode', 'Sender country code'), type: 'text' },
    { id: 'carrierName', label: t('ecmr.form.carrierName', 'Carrier name'), type: 'text', required: true },
    { id: 'carrierCompany', label: t('ecmr.form.carrierCompany', 'Carrier company'), type: 'text' },
    { id: 'carrierStreet', label: t('ecmr.form.carrierStreet', 'Carrier street'), type: 'text' },
    { id: 'carrierCity', label: t('ecmr.form.carrierCity', 'Carrier city'), type: 'text' },
    { id: 'carrierPostalCode', label: t('ecmr.form.carrierPostalCode', 'Carrier postal code'), type: 'text' },
    { id: 'carrierCountryCode', label: t('ecmr.form.carrierCountryCode', 'Carrier country code'), type: 'text' },
    { id: 'receiverName', label: t('ecmr.form.receiverName', 'Receiver name'), type: 'text', required: true },
    { id: 'receiverCompany', label: t('ecmr.form.receiverCompany', 'Receiver company'), type: 'text' },
    { id: 'receiverStreet', label: t('ecmr.form.receiverStreet', 'Receiver street'), type: 'text' },
    { id: 'receiverCity', label: t('ecmr.form.receiverCity', 'Receiver city'), type: 'text' },
    { id: 'receiverPostalCode', label: t('ecmr.form.receiverPostalCode', 'Receiver postal code'), type: 'text' },
    { id: 'receiverCountryCode', label: t('ecmr.form.receiverCountryCode', 'Receiver country code'), type: 'text' },
    { id: 'placeOfTakingOver', label: t('ecmr.form.placeOfTakingOver', 'Place of taking over'), type: 'text', required: true },
    { id: 'placeOfDelivery', label: t('ecmr.form.placeOfDelivery', 'Place of delivery'), type: 'text', required: true },
    { id: 'plannedPickupAt', label: t('ecmr.form.plannedPickupAt', 'Planned pickup'), type: 'datetime-local' },
    { id: 'plannedDeliveryAt', label: t('ecmr.form.plannedDeliveryAt', 'Planned delivery'), type: 'datetime-local' },
    { id: 'goodsDescription', label: t('ecmr.form.goodsDescription', 'Goods description'), type: 'textarea', required: true },
    { id: 'goodsPackagesCount', label: t('ecmr.form.goodsPackagesCount', 'Packages count'), type: 'number' },
    { id: 'goodsGrossWeightKg', label: t('ecmr.form.goodsGrossWeightKg', 'Gross weight (kg)'), type: 'number' },
    { id: 'goodsVolumeM3', label: t('ecmr.form.goodsVolumeM3', 'Volume (m3)'), type: 'number' },
    { id: 'goodsUnNumber', label: t('ecmr.form.goodsUnNumber', 'UN number'), type: 'text' },
    { id: 'goodsHsCode', label: t('ecmr.form.goodsHsCode', 'HS code'), type: 'text' },
    { id: 'goodsMarksAndNumbers', label: t('ecmr.form.goodsMarksAndNumbers', 'Marks and numbers'), type: 'text' },
    { id: 'vehicleRegistrationNumber', label: t('ecmr.form.vehicleRegistrationNumber', 'Vehicle registration'), type: 'text' },
    { id: 'trailerRegistrationNumber', label: t('ecmr.form.trailerRegistrationNumber', 'Trailer registration'), type: 'text' },
    { id: 'carrierInstructions', label: t('ecmr.form.carrierInstructions', 'Carrier instructions'), type: 'textarea' },
    { id: 'receiverInstructions', label: t('ecmr.form.receiverInstructions', 'Receiver instructions'), type: 'textarea' },
  ]
}

export function buildECmrGroups(t: Translate): CrudFormGroup[] {
  return [
    { id: 'document', title: t('ecmr.groups.document', 'Document'), fields: ['issueDate', 'referenceNumber', 'dispatchId', 'driverId', 'vehicleId'] },
    { id: 'sender', title: t('ecmr.groups.sender', 'Sender'), fields: ['senderName', 'senderCompany', 'senderStreet', 'senderCity', 'senderPostalCode', 'senderCountryCode'] },
    { id: 'carrier', title: t('ecmr.groups.carrier', 'Carrier'), fields: ['carrierName', 'carrierCompany', 'carrierStreet', 'carrierCity', 'carrierPostalCode', 'carrierCountryCode'] },
    { id: 'receiver', title: t('ecmr.groups.receiver', 'Receiver'), fields: ['receiverName', 'receiverCompany', 'receiverStreet', 'receiverCity', 'receiverPostalCode', 'receiverCountryCode'] },
    { id: 'route', title: t('ecmr.groups.route', 'Route'), fields: ['placeOfTakingOver', 'placeOfDelivery', 'plannedPickupAt', 'plannedDeliveryAt'] },
    { id: 'goods', title: t('ecmr.groups.goods', 'Goods'), fields: ['goodsDescription', 'goodsPackagesCount', 'goodsGrossWeightKg', 'goodsVolumeM3', 'goodsUnNumber', 'goodsHsCode', 'goodsMarksAndNumbers'] },
    { id: 'transport', title: t('ecmr.groups.transport', 'Transport'), fields: ['vehicleRegistrationNumber', 'trailerRegistrationNumber', 'carrierInstructions', 'receiverInstructions'] },
  ]
}

export function buildInitialECmrValues(): ECmrFormValues {
  return {
    issueDate: new Date().toISOString().slice(0, 10),
  }
}

export function buildECmrPayload(values: ECmrFormValues): Record<string, unknown> {
  return {
    issueDate: toIsoDateTime(values.issueDate ? `${values.issueDate}T00:00` : undefined),
    referenceNumber: toNullableText(values.referenceNumber),
    dispatchId: toNullableText(values.dispatchId),
    driverId: toNullableText(values.driverId),
    vehicleId: toNullableText(values.vehicleId),
    senderSnapshot: buildPartySnapshot({
      name: values.senderName,
      company: values.senderCompany,
      street: values.senderStreet,
      city: values.senderCity,
      postalCode: values.senderPostalCode,
      countryCode: values.senderCountryCode,
    }),
    carrierSnapshot: buildPartySnapshot({
      name: values.carrierName,
      company: values.carrierCompany,
      street: values.carrierStreet,
      city: values.carrierCity,
      postalCode: values.carrierPostalCode,
      countryCode: values.carrierCountryCode,
    }),
    receiverSnapshot: buildPartySnapshot({
      name: values.receiverName,
      company: values.receiverCompany,
      street: values.receiverStreet,
      city: values.receiverCity,
      postalCode: values.receiverPostalCode,
      countryCode: values.receiverCountryCode,
    }),
    placeOfTakingOver: toNullableText(values.placeOfTakingOver),
    placeOfDelivery: toNullableText(values.placeOfDelivery),
    plannedPickupAt: toIsoDateTime(values.plannedPickupAt),
    plannedDeliveryAt: toIsoDateTime(values.plannedDeliveryAt),
    goodsDescription: toNullableText(values.goodsDescription),
    goodsPackagesCount: toNullableNumber(values.goodsPackagesCount),
    goodsGrossWeightKg: toNullableNumber(values.goodsGrossWeightKg),
    goodsVolumeM3: toNullableNumber(values.goodsVolumeM3),
    goodsUnNumber: toNullableText(values.goodsUnNumber),
    goodsHsCode: toNullableText(values.goodsHsCode),
    goodsMarksAndNumbers: toNullableText(values.goodsMarksAndNumbers),
    vehicleRegistrationNumber: toNullableText(values.vehicleRegistrationNumber),
    trailerRegistrationNumber: toNullableText(values.trailerRegistrationNumber),
    carrierInstructions: toNullableText(values.carrierInstructions),
    receiverInstructions: toNullableText(values.receiverInstructions),
  }
}
